import React from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import { useAsync, Box, Text } from '@wix/cli-ui-kit';
import type { DeploymentTopologyEnvironmentType } from '@wix/cli-astro-definitions';
import { OnboardingState, useVeloClient } from '@wix/velo-client';
import { ActionStatus } from './ActionStatus.js';
import { PlatformLink } from '../PlatformLink/PlatformLink.js';
import { useUpdateDeploymentTopologyForSite } from '../../hooks/useUpdateDeploymentTopology.js';
import { useSiteConfig } from '../../model.js';

export const SiteTopologyAndLinks: FC<{
  environment: DeploymentTopologyEnvironmentType;
  previewId: string;
  previewUrl: string;
  onTopologyUpdated: () => void;
}> = ({ environment, previewId, previewUrl, onTopologyUpdated }) => {
  const { appId, siteId } = useSiteConfig();
  const updateDeploymentTopology = useUpdateDeploymentTopologyForSite(siteId);
  const veloClient = useVeloClient();

  const { status } = useAsync(async () => {
    const [, gitHubOnboarding] = await Promise.all([
      updateDeploymentTopology({
        siteUrl: previewUrl,
        armTag: previewId,
        environment,
      }),
      veloClient.getGitHubOnboardingStatus(appId),
    ]);

    onTopologyUpdated();

    return { gitHubOnboarding };
  }, []);

  return (
    <>
      <ActionStatus
        status={status}
        loadingText={'Updating deployment topology manifest...'}
        successText={'Deployment topology manifest updated successfully!'}
      />
      {match(status, {
        Error: () => null,
        Loading: () => null,
        Success: ({ result: { gitHubOnboarding } }) => (
          <Box rowGap={1} marginY={1} flexDirection="column">
            <Text>Open the preview on:</Text>
            <Box paddingLeft={2} flexDirection="column">
              <PlatformLink url={previewUrl} platform="site" />
              <PlatformLink
                url={`https://manage.wix.com/dashboard/${siteId}?apps-override=${previewId}`}
                platform="dashboard"
              />
              {gitHubOnboarding?.status === OnboardingState.GITHUB_ENABLED && (
                <PlatformLink
                  url={`https://vibe.wix.com/projects/${siteId}`}
                  platform="editor"
                />
              )}
            </Box>
          </Box>
        ),
      })}
    </>
  );
};
